import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
	DetailSummary,
	DetailTabs,
} from "@/controls/detail-panel/DetailPanel";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import type { Recipe } from "../models/Recipe";
import { RecipeStandsTable } from "./RecipeStandsTable";

interface FormErrors {
	Name?: string;
}

interface RecipeDetailViewerProps {
	recipe: Recipe | null;
	existingRecipes: Recipe[];
	isSaving: boolean;
	onSave: (recipe: Recipe) => void;
	onClose: () => void;
	onDuplicate: (recipe: Recipe) => void;
}

const TABS = ["stands", "info"] as const;
type RecipeTab = (typeof TABS)[number];

const URL_SCHEMA = {
	tab: urlParam.oneOf(TABS, "stands"),
};

export const RecipeDetailViewer = ({
	recipe,
	existingRecipes,
	isSaving,
	onSave,
	onClose,
	onDuplicate,
}: RecipeDetailViewerProps) => {
	const [formData, setFormData] = useState<Recipe | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewRecipe = !recipe?.Id;
	const [{ tab }, setUrlState] = useUrlState(URL_SCHEMA);
	const activeTab: RecipeTab = isNewRecipe ? "info" : tab;

	const validateForm = useMemo(() => {
		return (data: Recipe | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingRecipes.some(
					(r) =>
						r.Id !== data.Id &&
						r.ProductSizeId === data.ProductSizeId &&
						r.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists for this product";
				}
			}

			return newErrors;
		};
	}, [existingRecipes]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewRecipe && isFormValid;

	useEffect(() => {
		if (recipe) {
			setFormData(sanitizeNullStrings({ ...recipe }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [recipe]);

	const handleChange = (field: keyof Recipe, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof Recipe) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof Recipe, value: unknown) => {
		handleBlur(field);

		if (isNewRecipe) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = recipe?.[field];
		if (originalValue !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleSelectChange = (field: keyof Recipe, value: unknown) => {
		handleChange(field, value);
		setTouched((prev) => ({ ...prev, [field]: true }));

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		setErrors(currentErrors);

		if (isNewRecipe) return;

		if (recipe?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleNumericBlurSave = (field: keyof Recipe, rawValue: string) => {
		const value = rawValue === "" ? null : Number.parseFloat(rawValue);
		handleChange(field, value);
		handleBlurSave(field, value);
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!recipe) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a recipe to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	const productLabel = formData.ProductTypeName
		? `(${formData.ProductTypeName}) ${formData.ProductSizeName}`
		: formData.ProductSizeName;

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={isNewRecipe ? "New Recipe" : formData.Name}
				active={isNewRecipe ? undefined : recipe.Active}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					isNewRecipe ? (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					) : (
						<Button
							variant="contained"
							color="success"
							size="small"
							onClick={() => onDuplicate(formData)}
							disabled={isSaving}
						>
							DUPLICATE
						</Button>
					)
				}
			/>

			<DetailBody>
				{!isNewRecipe && (
					<DetailSummary
						items={[
							{ label: "Product", value: productLabel, grow: 2.2 },
							{ label: "Line Speed", value: formData.LineSpeed, grow: 0.8 },
							{
								label: "Start Temp",
								value: formData.StartTemperature,
								grow: 0.8,
							},
							{ label: "End Temp", value: formData.EndTemperature, grow: 0.8 },
							{
								label: "Compare",
								value: formData.EnableComparison ? "Yes" : "No",
								grow: 0.7,
							},
						]}
					/>
				)}

				<DetailTabs<RecipeTab>
					value={activeTab}
					onChange={(next) => setUrlState({ tab: next })}
					flush={activeTab === "stands"}
					tabs={[
						{ value: "info", label: "Recipe Info" },
						{ value: "stands", label: "Stands", disabled: isNewRecipe },
					]}
				>
					{activeTab === "stands" && formData.Id ? (
						<RecipeStandsTable recipeId={formData.Id} />
					) : (
						<>
							<DetailSection
								title="Basic Information"
								icon={<InfoOutlinedIcon />}
							>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<TextField
											label="Name"
											value={formData.Name}
											onChange={(e) => handleChange("Name", e.target.value)}
											onBlur={(e) => handleBlurSave("Name", e.target.value)}
											fullWidth
											size="small"
											required
											error={touched.Name && !!errors.Name}
											helperText={touched.Name && errors.Name}
											slotProps={{ inputLabel: { shrink: true } }}
										/>
									</Grid2>
									<Grid2 size={{ xs: 6, md: 3 }}>
										<FormControlLabel
											control={
												<Checkbox
													checked={formData.EnableComparison}
													onChange={(e) =>
														handleSelectChange(
															"EnableComparison",
															e.target.checked,
														)
													}
													size="small"
												/>
											}
											label="Compare"
											sx={{ ml: 0 }}
										/>
									</Grid2>
									<Grid2 size={{ xs: 6, md: 3 }}>
										<FormControlLabel
											control={
												<Checkbox
													checked={formData.Active}
													onChange={(e) =>
														handleSelectChange("Active", e.target.checked)
													}
													size="small"
												/>
											}
											label="Active"
											sx={{ ml: 0 }}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>

							<DetailSection
								title="Line Parameters"
								icon={<SpeedOutlinedIcon />}
							>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 4 }}>
										<TextField
											label="Line Speed"
											type="number"
											value={formData.LineSpeed ?? ""}
											onChange={(e) =>
												handleChange(
													"LineSpeed",
													e.target.value === ""
														? null
														: Number.parseInt(e.target.value, 10),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("LineSpeed", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 1 },
											}}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 4 }}>
										<TextField
											label="Start Temperature"
											type="number"
											value={formData.StartTemperature ?? ""}
											onChange={(e) =>
												handleChange(
													"StartTemperature",
													e.target.value === ""
														? null
														: Number.parseInt(e.target.value, 10),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave(
													"StartTemperature",
													e.target.value,
												)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 1 },
											}}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 4 }}>
										<TextField
											label="End Temperature"
											type="number"
											value={formData.EndTemperature ?? ""}
											onChange={(e) =>
												handleChange(
													"EndTemperature",
													e.target.value === ""
														? null
														: Number.parseInt(e.target.value, 10),
												)
											}
											onBlur={(e) =>
												handleNumericBlurSave("EndTemperature", e.target.value)
											}
											fullWidth
											size="small"
											slotProps={{
												inputLabel: { shrink: true },
												htmlInput: { step: 1 },
											}}
										/>
									</Grid2>
								</Grid2>
							</DetailSection>
						</>
					)}
				</DetailTabs>
			</DetailBody>
		</Box>
	);
};
